import { z } from "zod";

/**
 * Runtime validation for the customer-safe signal preview boundary.
 *
 * The preview is untrusted server input until this exact schema and the
 * submitted readable draft agree on every customer-visible fact.
 */

const decimalTextSchema = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/);
const signedDecimalTextSchema = z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/);
const tickerSchema = z.string().regex(/^[A-Z][A-Z0-9.-]{0,14}$/);
const micCodeSchema = z.string().regex(/^[A-Z0-9]{4}$/);
const assetTypeSchema = z.enum(["COMMON_STOCK", "ETF"]);
const positiveIntegerSchema = z.number().int().safe().positive();
const nonNegativeIntegerSchema = z.number().int().safe().nonnegative();

const signalPolicySchema = z.object({
  max_duration_days: positiveIntegerSchema,
  evaluation_interval_minutes: positiveIntegerSchema,
  max_alerts_per_watch: positiveIntegerSchema,
  cooldown_minutes: nonNegativeIntegerSchema,
  market_hours_only: z.boolean(),
}).strict();

/**
 * The server echoes the normalized Watch policy in the preview, which carries
 * the customer schedule fields plus the server-owned guardrail defaults that
 * are part of the documented normalized policy. Only these exact extra keys
 * are tolerated; the customer fields are still compared against the draft.
 */
const serverNormalizedPolicySchema = signalPolicySchema.extend({
  guardrail_block_conflict_levels: z.array(z.string()).optional(),
  guardrail_reeval_on_event_risk: z.boolean().optional(),
  guardrail_reeval_interval_minutes: nonNegativeIntegerSchema.optional(),
}).strict();

const exactPriceReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: z.literal("underlying.price"),
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: z.object({ ticker: tickerSchema }).strict(),
    parameters: z.object({}).strict(),
    operator: z.enum(["gte", "lte"]),
    threshold: decimalTextSchema,
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const percentageMoveReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: z.enum([
      "underlying.price_change.prior_regular_close",
      "underlying.price_change.regular_session_open",
    ]),
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: z.object({
      ticker: tickerSchema,
      mic_code: micCodeSchema,
      asset_type: assetTypeSchema,
      country_code: z.literal("US"),
    }).strict(),
    parameters: z.object({
      direction: z.enum(["UP", "DOWN"]),
      magnitude_percent: decimalTextSchema,
    }).strict(),
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const canonicalListingSelectorSchema = z.object({
  ticker: tickerSchema,
  mic_code: micCodeSchema,
  asset_type: assetTypeSchema,
  country_code: z.literal("US"),
}).strict();

const movingAverageRelationshipReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: z.enum([
      "underlying.completed_regular_close.relative_to_sma",
      "underlying.completed_regular_close.crosses_sma",
      "underlying.completed_regular_close.relative_to_ema",
      "underlying.completed_regular_close.crosses_ema",
    ]),
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: canonicalListingSelectorSchema,
    parameters: z.object({
      direction: z.enum(["ABOVE", "BELOW"]),
      window: z.enum(["20", "50", "200"]),
    }).strict(),
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const movingAverageCrossoverReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: z.enum([
      "underlying.sma.crosses_sma",
      "underlying.ema.crosses_ema",
    ]),
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: canonicalListingSelectorSchema,
    parameters: z.object({
      direction: z.enum(["ABOVE", "BELOW"]),
      window_pair: z.enum(["20_50", "20_200", "50_200"]),
    }).strict(),
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const momentumLevelReadableSignalDraftSchema = z.union([
  z.object({
    signal_watch_draft: z.object({
      schema_version: z.literal(1),
      job: z.literal("MARKET"),
      signal_id: z.enum([
        "underlying.rsi.relative_to_level",
        "underlying.rsi.crosses_level",
      ]),
      definition_version: z.literal(1),
      subject_scope: z.literal("UNDERLYING"),
      selectors: canonicalListingSelectorSchema,
      parameters: z.object({
        direction: z.enum(["ABOVE", "BELOW"]),
        level: decimalTextSchema,
      }).strict(),
      policy: signalPolicySchema,
    }).strict(),
  }).strict(),
  z.object({
    signal_watch_draft: z.object({
      schema_version: z.literal(1),
      job: z.literal("MARKET"),
      signal_id: z.enum([
        "underlying.roc.relative_to_level",
        "underlying.roc.crosses_level",
      ]),
      definition_version: z.literal(1),
      subject_scope: z.literal("UNDERLYING"),
      selectors: canonicalListingSelectorSchema,
      parameters: z.object({
        direction: z.enum(["ABOVE", "BELOW"]),
        level_percent: signedDecimalTextSchema,
      }).strict(),
      policy: signalPolicySchema,
    }).strict(),
  }).strict(),
]);

const momentumMacdReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: z.enum([
      "underlying.macd.line.crosses_signal",
      "underlying.macd.line.crosses_zero",
    ]),
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: canonicalListingSelectorSchema,
    parameters: z.object({ direction: z.enum(["ABOVE", "BELOW"]) }).strict(),
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const completedDailyVolatilityReadableSignalDraftSchema = z.union([
  z.object({
    signal_watch_draft: z.object({
      schema_version: z.literal(1), job: z.literal("MARKET"),
      signal_id: z.literal("underlying.atr.completed_daily.14.relative_to_level"),
      definition_version: z.literal(1), subject_scope: z.literal("UNDERLYING"),
      selectors: canonicalListingSelectorSchema,
      parameters: z.object({ direction: z.enum(["ABOVE", "BELOW"]), level_usd: decimalTextSchema }).strict(),
      policy: signalPolicySchema,
    }).strict(),
  }).strict(),
  z.object({
    signal_watch_draft: z.object({
      schema_version: z.literal(1), job: z.literal("MARKET"),
      signal_id: z.enum([
        "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level",
        "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level",
        "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level",
      ]),
      definition_version: z.literal(1), subject_scope: z.literal("UNDERLYING"),
      selectors: canonicalListingSelectorSchema,
      parameters: z.object({ direction: z.enum(["ABOVE", "BELOW"]), level: signedDecimalTextSchema }).strict(),
      policy: signalPolicySchema,
    }).strict(),
  }).strict(),
]);

const standardizedOptionChainSignalIdSchema = z.enum([
  "options_chain.atm_iv.standardized_30d.relative_to_level",
  "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level",
  "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level",
  "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level",
  "options_chain.expected_move.standardized_30d.relative_to_level",
  "options_chain.atm_quote_coverage.standardized_30d.relative_to_level",
  "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level",
]);

const standardizedOptionChainReadableSignalDraftSchema = z.object({
  signal_watch_draft: z.object({
    schema_version: z.literal(1),
    job: z.literal("MARKET"),
    signal_id: standardizedOptionChainSignalIdSchema,
    definition_version: z.literal(1),
    subject_scope: z.literal("UNDERLYING"),
    selectors: canonicalListingSelectorSchema,
    parameters: z.object({
      direction: z.enum(["ABOVE", "BELOW"]),
      level: signedDecimalTextSchema,
    }).strict(),
    policy: signalPolicySchema,
  }).strict(),
}).strict();

const readableSignalDraftSchema = z.union([
  exactPriceReadableSignalDraftSchema,
  percentageMoveReadableSignalDraftSchema,
  movingAverageRelationshipReadableSignalDraftSchema,
  movingAverageCrossoverReadableSignalDraftSchema,
  momentumLevelReadableSignalDraftSchema,
  momentumMacdReadableSignalDraftSchema,
  completedDailyVolatilityReadableSignalDraftSchema,
  standardizedOptionChainReadableSignalDraftSchema,
]);

const exactPriceCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("PRICE"),
    name: z.literal("Price"),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    operator: z.enum(["gte", "lte"]),
    operator_phrase: z.enum(["reaches or rises above", "reaches or falls below"]),
    threshold: z.object({
      value: decimalTextSchema,
      display: z.string().regex(/^\$(?:0|[1-9]\d*)(?:\.\d+)?$/),
    }).strict(),
  }).strict(),
  sentence: z.string(),
  policy: serverNormalizedPolicySchema,
}).strict();

const percentageMoveCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("PRICE"),
    name: z.literal("Percentage move"),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    direction: z.enum(["UP", "DOWN"]),
    magnitude_percent: z.object({
      value: decimalTextSchema,
      display: z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?%$/),
    }).strict(),
    reference: z.object({
      kind: z.enum(["PRIOR_REGULAR_CLOSE", "REGULAR_SESSION_OPEN"]),
      label: z.enum([
        "Previous regular-session close",
        "Today's regular-session open",
      ]),
    }).strict(),
  }).strict(),
  sentence: z.string(),
  policy: serverNormalizedPolicySchema,
}).strict();

const movingAverageRelationshipCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("TREND"),
    name: z.enum([
      "Price vs. simple moving average",
      "Price crosses simple moving average",
      "Price vs. exponential moving average",
      "Price crosses exponential moving average",
    ]),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    kind: z.enum(["STATE", "CROSSING"]),
    average: z.enum(["SMA", "EMA"]),
    direction: z.enum(["ABOVE", "BELOW"]),
    window: z.object({
      value: z.enum(["20", "50", "200"]),
      display: z.enum(["20 days", "50 days", "200 days"]),
    }).strict(),
  }).strict(),
  sentence: z.string(),
  behavior: z.enum([
    "The first eligible true completed-session observation can alert.",
    "A new crossing after this Watch starts is required.",
  ]),
  schedule: z.literal("After each completed market day"),
  policy: serverNormalizedPolicySchema,
}).strict();

const movingAverageCrossoverCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("TREND"),
    name: z.enum([
      "Simple moving-average crossover",
      "Exponential moving-average crossover",
    ]),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    kind: z.literal("CROSSING"),
    average: z.enum(["SMA", "EMA"]),
    direction: z.enum(["ABOVE", "BELOW"]),
    window_pair: z.object({
      value: z.enum(["20_50", "20_200", "50_200"]),
      display: z.string(),
    }).strict(),
  }).strict(),
  sentence: z.string(),
  behavior: z.literal("A new crossing after this Watch starts is required."),
  schedule: z.literal("After each completed market day"),
  policy: serverNormalizedPolicySchema,
}).strict();

const momentumCommonPreview = {
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("MOMENTUM"),
    name: z.enum([
      "RSI level",
      "RSI crosses a level",
      "MACD line crosses signal line",
      "MACD line crosses zero",
      "14-day rate of change",
      "14-day rate of change crosses a level",
    ]),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  sentence: z.string(),
  behavior: z.enum([
    "The first eligible true completed-session observation can alert.",
    "A new crossing after this Watch starts is required.",
  ]),
  schedule: z.literal("After each completed market day"),
  policy: serverNormalizedPolicySchema,
} as const;

const momentumLevelCreationPreviewSchema = z.object({
  ...momentumCommonPreview,
  condition: z.object({
    kind: z.enum(["STATE", "CROSSING"]),
    indicator: z.enum(["RSI", "ROC"]),
    direction: z.enum(["ABOVE", "BELOW"]),
    period: z.object({
      value: z.literal("14"),
      display: z.literal("14 completed market days"),
    }).strict(),
    level: z.object({
      value: signedDecimalTextSchema,
      display: z.string(),
    }).strict(),
  }).strict(),
}).strict();

const momentumMacdCreationPreviewSchema = z.object({
  ...momentumCommonPreview,
  condition: z.object({
    kind: z.literal("CROSSING"),
    indicator: z.literal("MACD"),
    comparison: z.enum(["SIGNAL_LINE", "ZERO"]),
    direction: z.enum(["ABOVE", "BELOW"]),
    periods: z.object({
      value: z.literal("12_26_9"),
      display: z.literal("12 / 26 / 9 completed market days"),
    }).strict(),
  }).strict(),
}).strict();

const completedDailyVolatilityCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.literal("VOLATILITY"),
    name: z.enum([
      "14-day average true range",
      "Bollinger position",
      "Bollinger band width",
      "20-day realized volatility",
    ]),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    kind: z.literal("STATE"),
    indicator: z.enum([
      "ATR14", "BOLLINGER_PERCENT_B_20_2", "BOLLINGER_BANDWIDTH_20_2", "REALIZED_VOLATILITY_20D",
    ]),
    direction: z.enum(["ABOVE", "BELOW"]),
    level: z.object({ value: signedDecimalTextSchema, display: z.string() }).strict(),
  }).strict(),
  sentence: z.string(),
  behavior: z.literal("The first eligible true completed-session observation can alert."),
  schedule: z.literal("After each completed market day"),
  policy: serverNormalizedPolicySchema,
}).strict();

const standardizedOptionChainMeasurementSchema = z.enum([
  "ATM_IV_30D",
  "IV_RV_SPREAD_30D_20D",
  "RISK_REVERSAL_30D_25_DELTA",
  "ATM_IV_TERM_SLOPE_60D_MINUS_30D",
  "EXPECTED_MOVE_30D",
  "ATM_QUOTE_COVERAGE_30D",
  "ATM_MEDIAN_RELATIVE_SPREAD_30D",
]);

const standardizedOptionChainCreationPreviewSchema = z.object({
  kind: z.literal("SIGNAL_WATCH"),
  job: z.literal("MARKET"),
  signal: z.object({
    family: z.enum(["VOLATILITY", "OPTIONS_CHAIN", "LIQUIDITY"]),
    name: z.enum([
      "30-day implied volatility",
      "Implied versus recent volatility",
      "30-day put/call skew",
      "30-to-60-day IV slope",
      "30-day expected move",
      "Options quote coverage",
      "Typical near-ATM spread",
    ]),
  }).strict(),
  subject: z.object({ ticker: tickerSchema }).strict(),
  condition: z.object({
    kind: z.literal("STATE"),
    measurement: standardizedOptionChainMeasurementSchema,
    direction: z.enum(["ABOVE", "BELOW"]),
    level: z.object({
      value: signedDecimalTextSchema,
      display: z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?%$/),
    }).strict(),
  }).strict(),
  sentence: z.string(),
  behavior: z.literal("The first eligible true complete-chain observation can alert."),
  schedule: z.literal("During regular market hours"),
  policy: serverNormalizedPolicySchema,
}).strict();

const signalCreationPreviewSchema = z.union([
  exactPriceCreationPreviewSchema,
  percentageMoveCreationPreviewSchema,
  movingAverageRelationshipCreationPreviewSchema,
  movingAverageCrossoverCreationPreviewSchema,
  momentumLevelCreationPreviewSchema,
  momentumMacdCreationPreviewSchema,
  completedDailyVolatilityCreationPreviewSchema,
  standardizedOptionChainCreationPreviewSchema,
]);

/** Customer-safe preview returned for a readable MARKET signal draft. */
export type WatchSignalCreationPreview = z.infer<typeof signalCreationPreviewSchema>;

function canonicalDecimal(value: string): string {
  if (/^-0(?:\.0+)?$/.test(value)) return "0";
  const [whole, fraction = ""] = value.split(".");
  const trimmedFraction = fraction.replace(/0+$/, "");
  return trimmedFraction ? `${whole}.${trimmedFraction}` : whole;
}

function decimalTimesOneHundred(value: string): string {
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ""] = unsigned.split(".");
  const shifted = `${whole}${fraction.padEnd(2, "0").slice(0, 2)}`.replace(/^0+(?=\d)/, "") || "0";
  const remainder = fraction.slice(2).replace(/0+$/, "");
  return canonicalDecimal(`${negative ? "-" : ""}${shifted}${remainder ? `.${remainder}` : ""}`);
}

type StandardizedOptionChainSignalId = z.infer<typeof standardizedOptionChainSignalIdSchema>;
type StandardizedOptionChainMeasurement = z.infer<typeof standardizedOptionChainMeasurementSchema>;

const STANDARDIZED_OPTION_CHAIN_PREVIEW_FACTS: Readonly<Record<
StandardizedOptionChainSignalId, {
  family: "VOLATILITY" | "OPTIONS_CHAIN" | "LIQUIDITY";
  name: z.infer<typeof standardizedOptionChainCreationPreviewSchema>["signal"]["name"];
  measurement: StandardizedOptionChainMeasurement;
  phrase: string;
}
>> = {
  "options_chain.atm_iv.standardized_30d.relative_to_level": {
    family: "VOLATILITY",
    name: "30-day implied volatility",
    measurement: "ATM_IV_30D",
    phrase: "standardized 30-day implied volatility",
  },
  "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level": {
    family: "VOLATILITY",
    name: "Implied versus recent volatility",
    measurement: "IV_RV_SPREAD_30D_20D",
    phrase: "standardized 30-day implied volatility minus completed-session 20-day realized volatility",
  },
  "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level": {
    family: "OPTIONS_CHAIN",
    name: "30-day put/call skew",
    measurement: "RISK_REVERSAL_30D_25_DELTA",
    phrase: "standardized 30-day 25-delta put/call implied-volatility difference",
  },
  "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level": {
    family: "OPTIONS_CHAIN",
    name: "30-to-60-day IV slope",
    measurement: "ATM_IV_TERM_SLOPE_60D_MINUS_30D",
    phrase: "standardized 60-day minus 30-day at-the-money implied-volatility slope",
  },
  "options_chain.expected_move.standardized_30d.relative_to_level": {
    family: "VOLATILITY",
    name: "30-day expected move",
    measurement: "EXPECTED_MOVE_30D",
    phrase: "standardized one-standard-deviation 30-day expected move",
  },
  "options_chain.atm_quote_coverage.standardized_30d.relative_to_level": {
    family: "LIQUIDITY",
    name: "Options quote coverage",
    measurement: "ATM_QUOTE_COVERAGE_30D",
    phrase: "share of the standardized near-ATM cohort with valid two-sided quotes",
  },
  "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level": {
    family: "LIQUIDITY",
    name: "Typical near-ATM spread",
    measurement: "ATM_MEDIAN_RELATIVE_SPREAD_30D",
    phrase: "coverage-qualified median relative spread across the standardized near-ATM cohort",
  },
};

/** Return true only when every safe preview fact matches the submitted draft. */
export function isMatchingSignalCreationPreview(
  preview: unknown,
  submittedPayload: unknown,
): preview is WatchSignalCreationPreview {
  const submitted = readableSignalDraftSchema.safeParse(submittedPayload);
  const received = signalCreationPreviewSchema.safeParse(preview);
  if (!submitted.success || !received.success) return false;

  const expected = submitted.data.signal_watch_draft;
  const actual = received.data;
  if (actual.subject.ticker !== expected.selectors.ticker
    || actual.policy.max_duration_days !== expected.policy.max_duration_days
    || actual.policy.evaluation_interval_minutes
      !== expected.policy.evaluation_interval_minutes
    || actual.policy.max_alerts_per_watch !== expected.policy.max_alerts_per_watch
    || actual.policy.cooldown_minutes !== expected.policy.cooldown_minutes
    || actual.policy.market_hours_only !== expected.policy.market_hours_only) {
    return false;
  }

  if (expected.signal_id === "underlying.price") {
    if (!("operator" in actual.condition)) return false;
    const threshold = canonicalDecimal(expected.threshold);
    const display = `$${threshold}`;
    const phrase = expected.operator === "gte"
      ? "reaches or rises above"
      : "reaches or falls below";
    return actual.condition.operator === expected.operator
      && actual.condition.operator_phrase === phrase
      && actual.condition.threshold.value === threshold
      && actual.condition.threshold.display === display
      && actual.sentence === `Alert me when ${expected.selectors.ticker} ${phrase} ${display}.`;
  }

  if (expected.signal_id.startsWith("underlying.completed_regular_close.")) {
    const submittedRelationship = movingAverageRelationshipReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedRelationship = movingAverageRelationshipCreationPreviewSchema.safeParse(preview);
    if (!submittedRelationship.success || !receivedRelationship.success) return false;
    const relationshipExpected = submittedRelationship.data.signal_watch_draft;
    const relationshipActual = receivedRelationship.data;
    const averageType = relationshipExpected.signal_id.endsWith("_sma") ? "SMA" : "EMA";
    const crossing = relationshipExpected.signal_id.includes(".crosses_");
    const averageName = averageType === "SMA"
      ? "simple moving average"
      : "exponential moving average";
    const direction = relationshipExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const sentence = crossing
      ? `Alert me when ${relationshipExpected.selectors.ticker}'s completed daily close crosses ${direction} its ${relationshipExpected.parameters.window}-day ${averageName} after this Watch starts.`
      : `Alert me when ${relationshipExpected.selectors.ticker}'s completed daily close is ${direction} its ${relationshipExpected.parameters.window}-day ${averageName}.`;
    return relationshipActual.signal.family === "TREND"
      && relationshipActual.signal.name
        === `Price ${crossing ? "crosses" : "vs."} ${averageName}`
      && relationshipActual.condition.kind === (crossing ? "CROSSING" : "STATE")
      && relationshipActual.condition.average === averageType
      && relationshipActual.condition.direction === relationshipExpected.parameters.direction
      && relationshipActual.condition.window.value === relationshipExpected.parameters.window
      && relationshipActual.condition.window.display === `${relationshipExpected.parameters.window} days`
      && relationshipActual.behavior === (crossing
        ? "A new crossing after this Watch starts is required."
        : "The first eligible true completed-session observation can alert.")
      && relationshipActual.schedule === "After each completed market day"
      && relationshipActual.sentence === sentence;
  }

  if (expected.signal_id === "underlying.sma.crosses_sma"
    || expected.signal_id === "underlying.ema.crosses_ema") {
    const submittedCrossover = movingAverageCrossoverReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedCrossover = movingAverageCrossoverCreationPreviewSchema.safeParse(preview);
    if (!submittedCrossover.success || !receivedCrossover.success) return false;
    const crossoverExpected = submittedCrossover.data.signal_watch_draft;
    const crossoverActual = receivedCrossover.data;
    const averageType = crossoverExpected.signal_id === "underlying.sma.crosses_sma" ? "SMA" : "EMA";
    const [fastWindow, slowWindow] = crossoverExpected.parameters.window_pair.split("_");
    const direction = crossoverExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const sentence = `Alert me when ${crossoverExpected.selectors.ticker}'s ${fastWindow}-day ${averageType} crosses ${direction} its ${slowWindow}-day ${averageType} after this Watch starts.`;
    return crossoverActual.signal.family === "TREND"
      && crossoverActual.signal.name === (averageType === "SMA"
        ? "Simple moving-average crossover"
        : "Exponential moving-average crossover")
      && crossoverActual.condition.kind === "CROSSING"
      && crossoverActual.condition.average === averageType
      && crossoverActual.condition.direction === crossoverExpected.parameters.direction
      && crossoverActual.condition.window_pair.value === crossoverExpected.parameters.window_pair
      && crossoverActual.condition.window_pair.display === `${fastWindow} / ${slowWindow} days`
      && crossoverActual.behavior === "A new crossing after this Watch starts is required."
      && crossoverActual.schedule === "After each completed market day"
      && crossoverActual.sentence === sentence;
  }

  if (expected.signal_id.startsWith("underlying.rsi.")
    || expected.signal_id.startsWith("underlying.roc.")) {
    const submittedMomentum = momentumLevelReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedMomentum = momentumLevelCreationPreviewSchema.safeParse(preview);
    if (!submittedMomentum.success || !receivedMomentum.success) return false;
    const momentumExpected = submittedMomentum.data.signal_watch_draft;
    const momentumActual = receivedMomentum.data;
    const rsi = momentumExpected.signal_id.startsWith("underlying.rsi.");
    const state = momentumExpected.signal_id.endsWith("relative_to_level");
    const expectedName = rsi
      ? state ? "RSI level" : "RSI crosses a level"
      : state ? "14-day rate of change" : "14-day rate of change crosses a level";
    const rawLevel = "level" in momentumExpected.parameters
      ? momentumExpected.parameters.level
      : momentumExpected.parameters.level_percent;
    const level = canonicalDecimal(rawLevel);
    const display = rsi ? level : `${level}%`;
    const direction = momentumExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const measurement = rsi ? "RSI" : "14-day price change";
    const sentence = state
      ? `Alert me when ${momentumExpected.selectors.ticker}'s completed-daily ${measurement} is at or ${direction} ${display}.`
      : `Alert me when ${momentumExpected.selectors.ticker}'s completed-daily ${measurement} crosses ${direction} ${display} after this Watch starts.`;
    return momentumActual.signal.family === "MOMENTUM"
      && momentumActual.signal.name === expectedName
      && momentumActual.condition.indicator === (rsi ? "RSI" : "ROC")
      && momentumActual.condition.kind === (state ? "STATE" : "CROSSING")
      && momentumActual.condition.direction === momentumExpected.parameters.direction
      && momentumActual.condition.period.value === "14"
      && momentumActual.condition.period.display === "14 completed market days"
      && momentumActual.condition.level.value === level
      && momentumActual.condition.level.display === display
      && momentumActual.behavior === (state
        ? "The first eligible true completed-session observation can alert."
        : "A new crossing after this Watch starts is required.")
      && momentumActual.schedule === "After each completed market day"
      && momentumActual.sentence === sentence;
  }

  if (expected.signal_id.startsWith("underlying.macd.")) {
    const submittedMomentum = momentumMacdReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedMomentum = momentumMacdCreationPreviewSchema.safeParse(preview);
    if (!submittedMomentum.success || !receivedMomentum.success) return false;
    const momentumExpected = submittedMomentum.data.signal_watch_draft;
    const momentumActual = receivedMomentum.data;
    const signalLine = momentumExpected.signal_id.endsWith("crosses_signal");
    const expectedName = signalLine ? "MACD line crosses signal line" : "MACD line crosses zero";
    const comparisonPhrase = signalLine ? "its signal line" : "zero";
    const direction = momentumExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const sentence = `Alert me when ${momentumExpected.selectors.ticker}'s completed-daily MACD line crosses ${direction} ${comparisonPhrase} after this Watch starts.`;
    return momentumActual.signal.family === "MOMENTUM"
      && momentumActual.signal.name === expectedName
      && momentumActual.condition.indicator === "MACD"
      && momentumActual.condition.kind === "CROSSING"
      && momentumActual.condition.comparison === (signalLine ? "SIGNAL_LINE" : "ZERO")
      && momentumActual.condition.direction === momentumExpected.parameters.direction
      && momentumActual.condition.periods.value === "12_26_9"
      && momentumActual.condition.periods.display === "12 / 26 / 9 completed market days"
      && momentumActual.behavior === "A new crossing after this Watch starts is required."
      && momentumActual.schedule === "After each completed market day"
      && momentumActual.sentence === sentence;
  }

  if (expected.signal_id.startsWith("options_chain.")) {
    const submittedChain = standardizedOptionChainReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedChain = standardizedOptionChainCreationPreviewSchema.safeParse(preview);
    if (!submittedChain.success || !receivedChain.success) return false;
    const chainExpected = submittedChain.data.signal_watch_draft;
    const chainActual = receivedChain.data;
    const facts = STANDARDIZED_OPTION_CHAIN_PREVIEW_FACTS[chainExpected.signal_id];
    const level = canonicalDecimal(chainExpected.parameters.level);
    const display = `${decimalTimesOneHundred(level)}%`;
    const directionPhrase = chainExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const sentence = `Alert me when ${chainExpected.selectors.ticker}'s ${facts.phrase} is at or ${directionPhrase} ${display}.`;
    return chainActual.signal.family === facts.family
      && chainActual.signal.name === facts.name
      && chainActual.condition.kind === "STATE"
      && chainActual.condition.measurement === facts.measurement
      && chainActual.condition.direction === chainExpected.parameters.direction
      && chainActual.condition.level.value === level
      && chainActual.condition.level.display === display
      && chainActual.sentence === sentence
      && chainActual.behavior === "The first eligible true complete-chain observation can alert."
      && chainActual.schedule === "During regular market hours";
  }

  if (expected.signal_id.includes("completed_daily")
    || expected.signal_id.startsWith("underlying.realized_volatility.")) {
    const submittedVolatility = completedDailyVolatilityReadableSignalDraftSchema.safeParse(submittedPayload);
    const receivedVolatility = completedDailyVolatilityCreationPreviewSchema.safeParse(preview);
    if (!submittedVolatility.success || !receivedVolatility.success) return false;
    const volatilityExpected = submittedVolatility.data.signal_watch_draft;
    const volatilityActual = receivedVolatility.data;
    const atr = volatilityExpected.signal_id.startsWith("underlying.atr.");
    const percentB = volatilityExpected.signal_id.includes("bollinger.percent_b");
    const bandwidth = volatilityExpected.signal_id.includes("bollinger.bandwidth");
    const rawLevel = "level_usd" in volatilityExpected.parameters
      ? volatilityExpected.parameters.level_usd : volatilityExpected.parameters.level;
    const level = canonicalDecimal(rawLevel);
    const indicator = atr ? "ATR14" : percentB ? "BOLLINGER_PERCENT_B_20_2"
      : bandwidth ? "BOLLINGER_BANDWIDTH_20_2" : "REALIZED_VOLATILITY_20D";
    const name = atr ? "14-day average true range" : percentB ? "Bollinger position"
      : bandwidth ? "Bollinger band width" : "20-day realized volatility";
    const display = atr ? `$${level}`
      : indicator === "REALIZED_VOLATILITY_20D" ? `${decimalTimesOneHundred(level)}%` : level;
    const measurement = atr ? "14-day average true range" : percentB ? "Bollinger %B"
      : bandwidth ? "Bollinger band width" : "20-day annualized realized volatility";
    const direction = volatilityExpected.parameters.direction === "ABOVE" ? "above" : "below";
    const sentence = `Alert me when ${volatilityExpected.selectors.ticker}'s completed-daily ${measurement} is at or ${direction} ${display}.`;
    return volatilityActual.signal.family === "VOLATILITY"
      && volatilityActual.signal.name === name
      && volatilityActual.condition.kind === "STATE"
      && volatilityActual.condition.indicator === indicator
      && volatilityActual.condition.direction === volatilityExpected.parameters.direction
      && volatilityActual.condition.level.value === level
      && volatilityActual.condition.level.display === display
      && volatilityActual.behavior === "The first eligible true completed-session observation can alert."
      && volatilityActual.schedule === "After each completed market day"
      && volatilityActual.sentence === sentence;
  }

  if (!("magnitude_percent" in expected.parameters)
    || !("direction" in actual.condition)
    || !("magnitude_percent" in actual.condition)
    || !("reference" in actual.condition)) return false;
  const magnitude = canonicalDecimal(expected.parameters.magnitude_percent);
  const reference = expected.signal_id === "underlying.price_change.prior_regular_close"
    ? {
      kind: "PRIOR_REGULAR_CLOSE" as const,
      label: "Previous regular-session close" as const,
      phrase: "the previous regular-session close",
    }
    : {
      kind: "REGULAR_SESSION_OPEN" as const,
      label: "Today's regular-session open" as const,
      phrase: "today's regular-session open",
    };
  const directionPhrase = expected.parameters.direction === "UP" ? "up" : "down";
  return actual.condition.direction === expected.parameters.direction
    && actual.condition.magnitude_percent.value === magnitude
    && actual.condition.magnitude_percent.display === `${magnitude}%`
    && actual.condition.reference.kind === reference.kind
    && actual.condition.reference.label === reference.label
    && actual.sentence === `Alert me when ${expected.selectors.ticker} is ${directionPhrase} at least ${magnitude}% from ${reference.phrase}.`;
}
